import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Episode } from '../types/episodes';
import Picture from './Picture';

type HeroCarouselProps = {
  episodes: Episode[];
  onNavigateToEpisode: (slug: string) => void;
};

export default function HeroCarousel({ episodes, onNavigateToEpisode }: HeroCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const randomEpisodes = useMemo(() => episodes.slice(0, 5), [episodes]);

  useEffect(() => {
    if (!isAutoPlaying || randomEpisodes.length === 0) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % randomEpisodes.length);
        setIsTransitioning(false);
      }, 300);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, randomEpisodes.length]);

  const nextSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % randomEpisodes.length);
      setIsTransitioning(false);
    }, 300);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + randomEpisodes.length) % randomEpisodes.length);
      setIsTransitioning(false);
    }, 300);
    setIsAutoPlaying(false);
  };

  if (randomEpisodes.length === 0) {
    return (
      <section id="home" className="relative h-screen flex items-center justify-center">
        <p className="text-white text-xl">Loading episodes...</p>
      </section>
    );
  }

  const currentEpisode = randomEpisodes[currentSlide];
  const storageRoot = currentEpisode.storage_root;
  const heroImageFilename = currentEpisode.carousel_slide_1080 || currentEpisode.youtube_thumbnail_1080p;

  return (
    <section id="home" className="relative h-screen overflow-hidden">
      <div
        className={`absolute inset-0 transition-all duration-700 ${
          isTransitioning ? 'opacity-0 scale-110' : 'opacity-100 scale-100'
        }`}
      >
        {heroImageFilename ? (
          <Picture
            storageRoot={storageRoot}
            filename={heroImageFilename}
            alt={currentEpisode.title}
            sizes="100vw"
            priority
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-black to-gray-900" />
        )}
        <div className="absolute inset-0 bg-black/40"></div>
      </div>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(139, 0, 139, 0.7) 0%, rgba(139, 0, 139, 0.4) 15%, rgba(0, 0, 0, 0) 35%)'
        }}
      ></div>

      <div className="relative h-full container mx-auto px-4 flex items-center">
        <div
          className={`max-w-3xl space-y-6 transition-all duration-500 ${
            isTransitioning
              ? 'opacity-0 translate-x-20'
              : 'opacity-100 translate-x-0'
          }`}
        >
          <div className="inline-block bg-red-500 text-white px-4 py-2 font-bold text-sm mb-2 transition-transform duration-300">
            {currentEpisode.podcast_number}
          </div>
          <h2 className="font-gellatio text-3xl md:text-8xl text-[#ffed00] neon-glow-yellow leading-relaxed whitespace-nowrap">
            {currentEpisode.game_project}
          </h2>
          <p className="text-white text-lg md:text-xl leading-relaxed">
            {currentEpisode.description?.substring(0, 200) || 'Watch this amazing episode'}...
          </p>
          <button
            onClick={() => onNavigateToEpisode(currentEpisode.slug)}
            className="bg-[#ffed00] text-black px-8 py-4 font-bold text-lg hover-scale hover:bg-[#ffed00]/90 transition-colors"
          >
            Watch Episode
          </button>
        </div>
      </div>

      <button
        onClick={prevSlide}
        aria-label="Previous episode"
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-3 rounded-full transition-colors"
        onMouseEnter={() => setIsAutoPlaying(false)}
      >
        <ChevronLeft className="text-white" size={32} />
      </button>

      <button
        onClick={nextSlide}
        aria-label="Next episode"
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-3 rounded-full transition-colors"
        onMouseEnter={() => setIsAutoPlaying(false)}
      >
        <ChevronRight className="text-white" size={32} />
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3" role="group" aria-label="Episode navigation">
        {randomEpisodes.map((episode, index) => (
          <button
            key={index}
            onClick={() => {
              setCurrentSlide(index);
              setIsAutoPlaying(false);
            }}
            aria-label={`Go to episode ${index + 1}: ${episode.title}`}
            aria-current={index === currentSlide ? 'true' : 'false'}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentSlide ? 'bg-[#ffed00] w-8' : 'bg-white/50 hover:bg-white'
            }`}
          />
        ))}
      </div>
    </section>
  );
}
